import clsx from 'clsx'
import { useCallback, useMemo } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { HugeiconsIcon, HugeiconsIconProps } from '@hugeicons/react'
import { Trans } from '@lingui/react/macro'

import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  SortByDown02Icon,
  SortByUp02Icon,
  Sorting05Icon,
  UnavailableIcon
} from '@hugeicons/core-solid-rounded'

import { EmptyView } from './EmptyView'

export function Root({
  children,
  ...props
}: React.TableHTMLAttributes<HTMLTableElement>) {
  return <table {...props}>{children}</table>
}

export function Head({
  children,
  ...props
}: React.TableHTMLAttributes<HTMLTableSectionElement>) {
  return <thead {...props}>{children}</thead>
}

export function Body({
  children,
  ...props
}: React.TableHTMLAttributes<HTMLTableSectionElement>) {
  return <tbody {...props}>{children}</tbody>
}

export function Foot({
  children,
  ...props
}: React.TableHTMLAttributes<HTMLTableSectionElement>) {
  return <tfoot {...props}>{children}</tfoot>
}

export function Row({
  children,
  onClick,
  onEnter,
  onKeyDown,
  ...props
}: React.TableHTMLAttributes<HTMLTableRowElement> & {
  onEnter?: (event: React.KeyboardEvent<HTMLTableRowElement>) => void
}) {
  const onKeyDownProxy = useCallback(
    (event: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (event.key === 'Enter') return onEnter?.(event)

      onKeyDown?.(event)
    },
    [onKeyDown, onEnter]
  )

  return (
    <tr
      {...props}
      onClick={onClick}
      onKeyDown={onKeyDownProxy}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </tr>
  )
}

export function Heading({
  children,
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return <th {...props}>{children}</th>
}

export function Cell({
  children,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <td {...props}>{children}</td>
}

export function Empty({
  children,
  Icon = UnavailableIcon,
  text,
  to,
  toText,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  Icon?: HugeiconsIconProps['icon']
  text: string
  to?: string
  toText?: string
}) {
  return (
    <Row className="pointer-events-none p-8">
      <Cell colSpan={100} {...props}>
        <EmptyView Icon={Icon} text={text} to={to} toText={toText}>
          {children}
        </EmptyView>
      </Cell>
    </Row>
  )
}

export function SortableHeading<T extends string>({
  children,
  sortField,
  currentSortField,
  currentSortDirection,
  onChangeSort
}: React.PropsWithChildren<{
  text?: string
  sortField: T
  currentSortField?: T | null
  currentSortDirection?: GraphQL.SortDirection | null
  onChangeSort: (field: T, direction: GraphQL.SortDirection) => void
}>) {
  const onChange = useCallback(() => {
    if (sortField !== currentSortField)
      return onChangeSort(sortField, GraphQL.SortDirection.Asc)

    if (currentSortDirection === GraphQL.SortDirection.Desc)
      return onChangeSort(sortField, GraphQL.SortDirection.Asc)

    if (currentSortDirection === GraphQL.SortDirection.Asc)
      return onChangeSort(sortField, GraphQL.SortDirection.Desc)
  }, [sortField, currentSortDirection, currentSortField, onChangeSort])

  return (
    <th align="center">
      <button
        className="flex cursor-pointer flex-row items-center justify-center gap-1"
        tabIndex={0}
        onClick={onChange}
      >
        <span>{children}</span>
        {currentSortField === sortField && (
          <>
            {currentSortDirection === GraphQL.SortDirection.Desc && (
              <HugeiconsIcon icon={SortByDown02Icon} className="size-4" />
            )}
            {currentSortDirection === GraphQL.SortDirection.Asc && (
              <HugeiconsIcon icon={SortByUp02Icon} className="size-4" />
            )}
          </>
        )}
        {currentSortField !== sortField && (
          <HugeiconsIcon icon={Sorting05Icon} className="size-4" />
        )}
      </button>
    </th>
  )
}

export function PaginationCaption({
  currentPage,
  totalPages,
  totalEntries,
  onChangePage
}: {
  currentPage: number
  totalPages: number
  totalEntries: number
  onChangePage: (page: number) => void
}) {
  const pages = useMemo(() => {
    return Array.from({ length: totalPages }, () => null).map(
      function (_, index) {
        const page = index + 1
        const isCurrent = currentPage === page

        return {
          page,
          isCurrent
        }
      }
    )
  }, [currentPage, totalPages])

  return (
    <caption>
      <div className="flex flex-col items-end justify-center">
        <div className="flex w-full flex-row items-center justify-center gap-1.5">
          {currentPage > 1 && (
            <button
              className="text-info"
              tabIndex={0}
              onClick={() => onChangePage(currentPage - 1)}
            >
              <HugeiconsIcon icon={ArrowLeft01Icon} className="size-4" />
            </button>
          )}
          {pages.map(function ({ isCurrent, page }) {
            return (
              <button
                key={page}
                onClick={() => onChangePage(page)}
                disabled={isCurrent}
                className={clsx('text-info', isCurrent && 'font-bold')}
              >
                {page}
              </button>
            )
          })}
          {currentPage < totalPages && (
            <button
              className="text-info"
              tabIndex={0}
              onClick={() => onChangePage(currentPage + 1)}
            >
              <HugeiconsIcon icon={ArrowRight01Icon} className="size-4" />
            </button>
          )}
        </div>
        <span className="text-small">
          <Trans>{totalEntries} total entries</Trans>
        </span>
      </div>
    </caption>
  )
}
